/**
 * Centralized Error Handler
 */

import { ApiError, NetworkError, ServerError, UnauthorizedError } from "./api-error";
import { logger } from "../logger";

export function handleApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof Error) {
    // Network errors
    if (
      error.message.includes("fetch") ||
      error.message.includes("network") ||
      error.message.includes("Failed to fetch")
    ) {
      logger.error("Network error", { error: error.message });
      return new NetworkError(error.message);
    }

    // Unknown errors
    logger.error("Unknown error", { error: error.message, stack: error.stack });
    return new ServerError(error.message);
  }

  // Fallback for unknown error types
  logger.error("Unknown error type", { error });
  return new ServerError("An unexpected error occurred");
}

/**
 * Log a failed request at the level it deserves.
 *
 * A 4xx is the endpoint answering the visitor -- email already registered, wrong OTP,
 * too many attempts -- and every one of these call sites puts that message straight on
 * the screen. Recording it at error level filed an ordinary form outcome next to real
 * breakage in Azure, and raised a Console Error card in the dev overlay for each one.
 * Anything else -- a 5xx, a dropped connection, something that never reached the
 * endpoint at all -- is still an error.
 */
export function logRequestFailure(
  message: string,
  error: unknown,
  context?: Record<string, unknown>,
) {
  const status = error instanceof ApiError ? error.status : 0;
  const expected = status >= 400 && status < 500;

  logger[expected ? "warn" : "error"](message, { ...context, error });
}

// Same-origin fetch helpers (getJson/postJson-style) call this after a failed
// response so a 401 becomes an UnauthorizedError instead of a generic ServerError -
// that's what lets the global query/mutation cache in lib/query-client.ts recognize
// an expired/missing session and redirect to /login instead of leaving the page stuck.
export function throwForFailedResponse(res: Response, data: unknown, fallbackMessage: string): never {
  const message =
    data && typeof data === "object" && "message" in data && typeof data.message === "string"
      ? data.message
      : fallbackMessage;

  if (res.status === 401) {
    throw new UnauthorizedError(message);
  }
  // Kept as a 429 so a retry is not fired straight back into the same rate-limit window.
  if (res.status === 429) {
    throw new ApiError(429, message, "RATE_LIMITED");
  }
  throw handleApiError(new Error(message));
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}
